import { DynamicRequestForm, type TemplateField } from '@/components/features/ServiceRequests/DynamicRequestForm';
import { SignoffPanel, type SignoffData } from '@/components/features/ServiceRequests/SignoffPanel';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import ShowLayout from '@/layouts/show-layout';
import { formatDateForDisplay, formatDateTime } from '@/lib/utils';
import { type PageProps } from '@/types';
import { Head, Link, useForm, usePage } from '@inertiajs/react';
import { ArrowLeft, CalendarIcon, Paperclip, Printer, UserRound } from 'lucide-react';
import { route } from 'ziggy-js';

interface AdminShowPageProps extends PageProps {
    [key: string]: unknown;
    request: {
        id: string;
        public_id: string | null;
        title: string;
        status: string;
        priority: string;
        due_date: string | null;
        requester_notes: string | null;
        admin_notes: string | null;
        resolution_notes: string | null;
        submitted_data: Record<string, unknown> | null;
        template_snapshot: { fields?: TemplateField[] } | null;
        template_name: string | null;
        assigned_to_user_id: string | null;
        requester: { id: string; name: string; email: string } | null;
        assignee: { id: string; name: string } | null;
        linkable_type: string | null;
        linkable_id: string | null;
        created_at: string | null;
        updated_at: string | null;
        resolved_at: string | null;
        closed_at: string | null;
        attachments: Array<{
            id: string;
            file_name: string;
            mime_type: string | null;
            file_size: number | null;
            uploaded_by: string | null;
            created_at: string | null;
            download_url: string;
        }>;
        events: Array<{
            id: string;
            event_type: string;
            old_value: Record<string, unknown> | null;
            new_value: Record<string, unknown> | null;
            note: string | null;
            actor: string | null;
            created_at: string | null;
        }>;
        approval: SignoffData | null;
    };
    options: {
        statuses: string[];
        priorities: string[];
        users: Array<{ id: string; name: string }>;
    };
}

const statusBadgeClassMap: Record<string, string> = {
    open: 'bg-info-subtle text-info-subtle-foreground',
    in_progress: 'bg-warning-subtle text-warning-subtle-foreground border-warning-subtle-foreground/30',
    on_hold: 'bg-warning-subtle text-warning-subtle-foreground',
    resolved: 'bg-success-subtle text-success-subtle-foreground',
    closed: 'bg-muted text-muted-foreground',
    rejected: 'bg-danger-subtle text-danger-subtle-foreground',
};

const priorityBadgeClassMap: Record<string, string> = {
    low: 'bg-muted text-muted-foreground',
    normal: 'bg-info-subtle text-info-subtle-foreground',
    high: 'bg-warning-subtle text-warning-subtle-foreground',
    urgent: 'bg-danger-subtle text-danger-subtle-foreground',
};

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="grid grid-cols-[140px_1fr] items-start gap-2 py-2.5 sm:grid-cols-[180px_1fr] sm:gap-4 sm:py-3">
            <div className="text-muted-foreground pt-0.5 text-sm">{label}</div>
            <div className="text-sm font-medium">{children}</div>
        </div>
    );
}

export default function AdminServiceRequestShowPage() {
    const { props } = usePage<AdminShowPageProps>();
    const { request, options } = props;
    const { t } = useTranslations();

    const { data, setData, patch, processing } = useForm({
        status: request.status,
        priority: request.priority,
        assigned_to_user_id: request.assigned_to_user_id ?? '',
        due_date: request.due_date ?? '',
        admin_notes: request.admin_notes ?? '',
        resolution_notes: request.resolution_notes ?? '',
        linkable_type: request.linkable_type ?? '',
        linkable_id: request.linkable_id ?? '',
    });

    const submit = () => patch(route('admin.service-requests.update', request.id), { preserveScroll: true });

    const fields: TemplateField[] = request.template_snapshot?.fields ?? [];
    const submittedData = request.submitted_data ?? {};

    const breadcrumbs = [
        { title: t('service_requests.admin_queue_title', 'Service Request Queue'), href: '/admin/service-requests' },
        { title: request.public_id ?? request.id.slice(0, 8), href: `/admin/service-requests/${request.id}` },
    ];

    const sidebar = (
        <div className="bg-card rounded-xl border p-5">
            <h3 className="mb-4 text-sm font-semibold tracking-wide uppercase">Admin Actions</h3>
            <div className="space-y-4">
                <div className="space-y-2">
                    <Label className="text-sm">{t('service_requests.status', 'Status')}</Label>
                    <Select value={data.status} onValueChange={(v) => setData('status', v)}>
                        <SelectTrigger className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {options.statuses.map((s) => (
                                <SelectItem key={s} value={s}>
                                    {s}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label className="text-sm">{t('service_requests.priority', 'Priority')}</Label>
                    <Select value={data.priority} onValueChange={(v) => setData('priority', v)}>
                        <SelectTrigger className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {options.priorities.map((p) => (
                                <SelectItem key={p} value={p}>
                                    {p}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label className="inline-flex items-center gap-2 text-sm">
                        <UserRound className="h-4 w-4" />
                        {t('service_requests.assignee', 'Assignee')}
                    </Label>
                    <Select value={data.assigned_to_user_id || 'none'} onValueChange={(v) => setData('assigned_to_user_id', v === 'none' ? '' : v)}>
                        <SelectTrigger className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">{t('service_requests.unassigned', 'Unassigned')}</SelectItem>
                            {options.users.map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                    {u.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label className="inline-flex items-center gap-2 text-sm">
                        <CalendarIcon className="h-4 w-4" />
                        {t('service_requests.due_date', 'Due Date')}
                    </Label>
                    <Input type="date" value={data.due_date} onChange={(e) => setData('due_date', e.target.value)} />
                </div>

                <div className="space-y-2">
                    <Label className="text-sm">{t('service_requests.admin_notes', 'Internal notes')}</Label>
                    <Textarea value={data.admin_notes} onChange={(e) => setData('admin_notes', e.target.value)} rows={4} />
                </div>

                <div className="space-y-2">
                    <Label className="text-sm">{t('service_requests.resolution_notes', 'Resolution notes')}</Label>
                    <Textarea value={data.resolution_notes} onChange={(e) => setData('resolution_notes', e.target.value)} rows={4} />
                </div>

                <div className="space-y-2">
                    <Label className="text-sm">Linked entity type</Label>
                    <Input
                        value={data.linkable_type}
                        onChange={(e) => setData('linkable_type', e.target.value)}
                        placeholder="e.g. App\\Models\\BugReport"
                    />
                </div>

                <div className="space-y-2">
                    <Label className="text-sm">Linked entity ID</Label>
                    <Input value={data.linkable_id} onChange={(e) => setData('linkable_id', e.target.value)} placeholder="UUID" />
                </div>

                <Button type="button" className="w-full" onClick={submit} disabled={processing}>
                    {processing ? t('service_requests.saving', 'Saving...') : t('service_requests.save_changes', 'Save changes')}
                </Button>
            </div>
        </div>
    );

    return (
        <>
            <Head title={request.public_id ?? 'Service Request'} />
            <ShowLayout breadcrumbs={breadcrumbs} title={request.public_id ?? 'Service Request'} sidebar={sidebar} mobileSidebarTitle="Admin Actions">
                <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <Button variant="ghost" size="sm" asChild>
                            <Link href={route('admin.service-requests.index')}>
                                <ArrowLeft className="mr-1 h-4 w-4" />
                                {t('service_requests.back_to_queue', 'Back to queue')}
                            </Link>
                        </Button>
                        {request.approval?.status === 'approved' && (
                            <Button variant="outline" size="sm" asChild>
                                <a href={route('service-requests.pdf', request.id)} target="_blank" rel="noreferrer">
                                    <Printer className="mr-1 h-4 w-4" />
                                    {t('service_requests.signoff.print', 'Print clearance')}
                                </a>
                            </Button>
                        )}
                    </div>

                    <div className="bg-card rounded-xl border p-5">
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                            <span className="text-muted-foreground font-mono text-xs">{request.public_id}</span>
                            <Badge variant="outline" className={statusBadgeClassMap[request.status] ?? ''}>
                                {request.status}
                            </Badge>
                            <Badge variant="outline" className={priorityBadgeClassMap[request.priority] ?? ''}>
                                {request.priority}
                            </Badge>
                        </div>
                        <h2 className="mb-3 text-sm font-semibold tracking-wide uppercase">Overview</h2>
                        <div className="divide-y">
                            <DetailRow label={t('service_requests.template', 'Template')}>{request.template_name ?? '—'}</DetailRow>
                            <DetailRow label={t('service_requests.requester', 'Requester')}>
                                {request.requester?.name ?? '—'}{' '}
                                {request.requester?.email ? <span className="text-muted-foreground text-xs">({request.requester.email})</span> : null}
                            </DetailRow>
                            <DetailRow label={t('service_requests.assignee', 'Assignee')}>
                                {request.assignee?.name ?? (
                                    <span className="text-muted-foreground">{t('service_requests.unassigned', 'Unassigned')}</span>
                                )}
                            </DetailRow>
                            <DetailRow label={t('service_requests.due_date', 'Due Date')}>
                                {request.due_date ? formatDateForDisplay(request.due_date) : <span className="text-muted-foreground">—</span>}
                            </DetailRow>
                            <DetailRow label={t('service_requests.created_at', 'Submitted')}>
                                {request.created_at ? formatDateTime(request.created_at) : '—'}
                            </DetailRow>
                            {request.resolved_at && (
                                <DetailRow label={t('service_requests.status_resolved', 'Resolved')}>{formatDateTime(request.resolved_at)}</DetailRow>
                            )}
                            {request.linkable_type && (
                                <DetailRow label="Linked to">
                                    {request.linkable_type.split('\\').pop()} #{request.linkable_id?.slice(0, 8)}
                                </DetailRow>
                            )}
                        </div>
                    </div>

                    {request.approval && <SignoffPanel requestId={request.id} approval={request.approval} />}

                    {fields.length > 0 && (
                        <div className="bg-card rounded-xl border p-5">
                            <h2 className="mb-5 text-sm font-semibold tracking-wide uppercase">
                                {t('service_requests.submitted_data', 'Request Details')}
                            </h2>
                            <DynamicRequestForm fields={fields} data={submittedData} users={options.users} onChange={() => {}} readOnly />
                        </div>
                    )}

                    {request.requester_notes && (
                        <div className="bg-card rounded-xl border p-5">
                            <h2 className="mb-3 text-sm font-semibold tracking-wide uppercase">
                                {t('service_requests.requester_notes', 'Requester notes')}
                            </h2>
                            <p className="text-sm leading-7 whitespace-pre-wrap">{request.requester_notes}</p>
                        </div>
                    )}

                    <div className="bg-card rounded-xl border p-5">
                        <div className="mb-3 flex items-center gap-2">
                            <Paperclip className="text-muted-foreground h-4 w-4" />
                            <h2 className="text-sm font-semibold tracking-wide uppercase">{t('service_requests.attachments', 'Attachments')}</h2>
                        </div>
                        {request.attachments.length === 0 ? (
                            <p className="text-muted-foreground text-sm">{t('service_requests.no_attachments', 'No attachments.')}</p>
                        ) : (
                            <div className="space-y-2">
                                {request.attachments.map((a) => (
                                    <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3">
                                        <div>
                                            <p className="text-sm font-medium">{a.file_name}</p>
                                            <p className="text-muted-foreground text-xs">
                                                {a.mime_type ?? 'file'}
                                                {a.file_size ? ` · ${(a.file_size / 1024).toFixed(1)} KB` : ''}
                                                {a.uploaded_by ? ` · ${a.uploaded_by}` : ''}
                                            </p>
                                        </div>
                                        <Button asChild variant="outline" size="sm">
                                            <a href={a.download_url} target="_blank" rel="noreferrer">
                                                {t('service_requests.download', 'Download')}
                                            </a>
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="bg-card rounded-xl border p-5">
                        <h2 className="mb-4 text-sm font-semibold tracking-wide uppercase">{t('service_requests.activity', 'Activity')}</h2>
                        {request.events.length === 0 ? (
                            <p className="text-muted-foreground text-sm">{t('service_requests.no_activity', 'No activity yet.')}</p>
                        ) : (
                            <div className="space-y-3">
                                {request.events.map((e) => (
                                    <div key={e.id} className="rounded-lg border p-3">
                                        <p className="text-sm font-medium">{e.event_type}</p>
                                        <p className="text-muted-foreground mt-0.5 text-xs">
                                            {e.actor ?? t('service_requests.system', 'System')} · {e.created_at ? formatDateTime(e.created_at) : '—'}
                                        </p>
                                        {e.note && <p className="mt-2 text-sm">{e.note}</p>}
                                        {e.new_value && (
                                            <pre className="bg-muted mt-2 max-w-full overflow-x-auto rounded-lg p-2 text-xs">
                                                {JSON.stringify(e.new_value, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </ShowLayout>
        </>
    );
}
